import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPhone, FaEnvelope, FaMapMarkerAlt, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import useColleges from '../../../utils/useColleges';
import './Defaulter.css';

const Defaulter = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = isSuperAdmin || user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();

  const [defaulters, setDefaulters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterFeeName, setFilterFeeName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMedium, setFilterMedium] = useState('');

  const loadDefaulters = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCollege) params.college = filterCollege;
      const res = await api.get('/payments/defaulters', { params });
      
      const data = (res.data.data || []).filter(d =>
        d.status === 'pending' || d.status === 'partial'
      );
      setDefaulters(data);
    } catch (err) {
      console.error(err);
      setDefaulters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDefaulters();
  }, [filterCollege]);

  useEffect(() => {
    if (!isAdmin && !isSuperAdmin && colleges.length > 0) setFilterCollege(String(colleges[0]._id));
    else if (isAdmin && !isSuperAdmin && colleges.length === 1) setFilterCollege(String(colleges[0]._id));
  }, [isAdmin, isSuperAdmin, colleges]);

  // Derived filter options
  const uniqueSessions = useMemo(() => {
    const map = new Map();
    defaulters.forEach(d => {
      const s = d.session;
      if (s) map.set(s._id || s, s.name || s);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [defaulters]);

  const uniqueClasses = useMemo(() => {
    const map = new Map();
    defaulters.forEach(d => {
      const c = d.student?.class;
      if (c) {
        const id = c._id || c;
        const medium = c.medium ? ` (${c.medium})` : '';
        const label = (c.name || c) + medium;
        map.set(id, label);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [defaulters]);

  const uniqueBatches = useMemo(() => {
    const set = new Set();
    defaulters.forEach(d => {
      const b = d.student?.admissionYear;
      if (b) set.add(b);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [defaulters]);

  const uniqueMediums = useMemo(() => {
    const set = new Set();
    defaulters.forEach(d => { const m = d.student?.class?.medium || d.student?.medium; if (m) set.add(m); });
    return Array.from(set).sort();
  }, [defaulters]);

  const uniqueFeeNames = useMemo(() => {
    const set = new Set();
    defaulters.forEach(d => { if (d.feeName) set.add(d.feeName); });
    return Array.from(set).sort();
  }, [defaulters]);

  const filteredDefaulters = useMemo(() => {
    let list = defaulters;
    if (filterCollege) list = list.filter(d => String(d.college?._id || d.college) === filterCollege);
    if (filterSession) list = list.filter(d => String(d.session?._id || d.session) === filterSession);
    if (filterClass)   list = list.filter(d => String(d.student?.class?._id || d.student?.class) === filterClass);
    if (filterMedium)  list = list.filter(d => (d.student?.class?.medium || d.student?.medium || '') === filterMedium);
    if (filterBatch)   list = list.filter(d => String(d.student?.admissionYear) === filterBatch);
    if (filterFeeName) list = list.filter(d => d.feeName === filterFeeName);
    if (filterStatus)  list = list.filter(d => d.status === filterStatus);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(d =>
        (d.student?.name || '').toLowerCase().includes(q) ||
        (d.student?.fatherName || '').toLowerCase().includes(q) ||
        (d.student?.admissionNo || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [defaulters, filterCollege, filterSession, filterClass, filterMedium, filterBatch, filterFeeName, searchQuery]);

  // Filter defaulters first, then group by college
  const groupedDefaulters = useMemo(() => {
    const activeCollegeIds = colleges.map(c => String(c._id));
    
    // First filter the defaulters
    let filtered = defaulters.filter(d => {
      const collegeId = String(d.college?._id || d.college);
      // Only include if college is in active colleges list
      if (!activeCollegeIds.includes(collegeId)) return false;
      
      // Apply college filter
      if (filterCollege && collegeId !== filterCollege) return false;
      
      // Apply session filter
      if (filterSession && String(d.session?._id || d.session) !== filterSession) return false;
      
      // Apply class filter
      if (filterClass && String(d.student?.class?._id || d.student?.class) !== filterClass) return false;

      // Apply medium filter
      if (filterMedium && (d.student?.class?.medium || d.student?.medium || '') !== filterMedium) return false;
      
      // Apply batch filter
      if (filterBatch && String(d.student?.admissionYear) !== filterBatch) return false;
      
      // Apply fee name filter
      if (filterFeeName && d.feeName !== filterFeeName) return false;
      
      // Apply status filter
      if (filterStatus && d.status !== filterStatus) return false;
      
      // Apply search query
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchName = (d.student?.name || '').toLowerCase().includes(q);
        const matchFather = (d.student?.fatherName || '').toLowerCase().includes(q);
        const matchAdmNo = (d.student?.admissionNo || '').toLowerCase().includes(q);
        if (!matchName && !matchFather && !matchAdmNo) return false;
      }
      
      return true;
    });
    
    // Then group by college
    const g = {};
    filtered.forEach(d => {
      const key = d.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(d);
    });
    return g;
  }, [defaulters, colleges, filterCollege, filterSession, filterClass, filterMedium, filterBatch, filterFeeName, filterStatus, searchQuery]);

  // Per-college filtered list - NO LONGER NEEDED, filters applied in groupedDefaulters
  const getFilteredList = (list) => {
    // All filtering is now done in groupedDefaulters, just return the list
    return list;
  };

  // ==========================================
  // PERFECT EXPORT TO EXCEL
  // ==========================================
  const handleExportExcel = () => {
    const today = new Date().toISOString().split('T')[0];
    const headers = [
      'S.No', 'Student Name', 'Father Name', 'Batch', 'Session', 'Year',
      'Class', 'Fee Name', 'Fee (₹)', 'Late Fee (₹)', 'Total Fee (₹)',
      'Deposit (₹)', 'Due (₹)', 'Status', 'Phone', 'Email', 'Address'
    ];

    const activeCollege = colleges.find(c => c._id === filterCollege)?.name || 'All Colleges';
    const activeSession = uniqueSessions.find(s => s.id === filterSession)?.name || 'All';
    const activeClass = uniqueClasses.find(c => c.id === filterClass)?.name || 'All';
    
    const filtersText = `Filters Applied -> College: ${activeCollege} | Session: ${activeSession} | Class: ${activeClass} | Batch: ${filterBatch || 'All'} | Fee Name: ${filterFeeName || 'All'} | Search: ${searchQuery || 'None'}`;

    const colsCount = headers.length;
    const aoa = [
      ['FEE DUE LIST REPORT', ...Array(colsCount - 1).fill('')],
      [filtersText, ...Array(colsCount - 1).fill('')],
      Array(colsCount).fill('')
    ];

    let merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colsCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colsCount - 1 } }
    ];

    let currentRow = 3; // 0-indexed

    Object.entries(groupedDefaulters).forEach(([collegeName, list]) => {
      // 1. College Header Row (Centered and Bold)
      aoa.push([collegeName.toUpperCase(), ...Array(colsCount - 1).fill('')]);
      merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: colsCount - 1 } });
      currentRow++;

      // 2. Column Headers
      aoa.push(headers);
      currentRow++;

      // 3. Data Rows
      list.forEach((item, index) => {
        const student = item.student || {};
        const fee = Number(item.amountDue || 0);
        const lateFee = Number(item.lateFee || 0);
        const totalFee = fee + lateFee;
        const deposit = Number(item.amountPaid || 0);
        const due = Number(item.balance || 0);
        const cObj = student.class || {};
        const className = (cObj.name || student.class || '-') + (cObj.medium ? ` (${cObj.medium})` : '');

        aoa.push([
          index + 1,
          student.name || '-',
          student.fatherName || '-',
          student.admissionYear || '-',
          item.session?.name || '-',
          student.currentYear || '-',
          className,
          item.feeName || '-',
          fee,
          lateFee,
          totalFee,
          deposit,
          due,
          item.status === 'partial' ? 'Partial' : 'Pending',
          student.contact || '-',
          student.email || '-',
          student.address || '-'
        ]);
        currentRow++;
      });

      // Add empty space between colleges
      aoa.push(Array(colsCount).fill(''));
      currentRow++;
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;

    // Properly sized columns for perfect display
    ws['!cols'] = [
      { wch: 8 },  // S.No
      { wch: 25 }, // Student Name
      { wch: 25 }, // Father Name
      { wch: 12 }, // Batch
      { wch: 15 }, // Session
      { wch: 10 }, // Year
      { wch: 20 }, // Class
      { wch: 25 }, // Fee Name
      { wch: 12 }, // Fee
      { wch: 12 }, // Late Fee
      { wch: 15 }, // Total Fee
      { wch: 15 }, // Deposit
      { wch: 15 }, // Due
      { wch: 12 }, // Status
      { wch: 18 }, // Phone
      { wch: 30 }, // Email
      { wch: 45 }  // Address
    ];

    const border = {
      top: { style: 'thin', color: { auto: 1 } }, bottom: { style: 'thin', color: { auto: 1 } },
      left: { style: 'thin', color: { auto: 1 } }, right: { style: 'thin', color: { auto: 1 } }
    };

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[ref]) ws[ref] = { v: '', t: 's' };

        if (R === 0) { // Title
          ws[ref].s = { font: { name: 'Arial', sz: 16, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1E3A5F' } }, alignment: { vertical: 'center', horizontal: 'center' } };
        } else if (R === 1) { // Filters
          ws[ref].s = { font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '2C3E50' } }, fill: { fgColor: { rgb: 'F1F5F9' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (ws[ref].v && typeof ws[ref].v === 'string' && ws[ref].v === ws[ref].v.toUpperCase() && C === 0 && !headers.includes(ws[ref].v) && R > 2) {
           // College Name Header Row
           ws[ref].s = { font: { name: 'Arial', sz: 13, bold: true, color: { rgb: '1E3A5F' } }, fill: { fgColor: { rgb: 'E2E8F0' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (headers.includes(ws[ref].v)) { // Table Headers
           ws[ref].s = { font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '34495E' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (R > 2 && ws[ref].v !== '') { // Data Cells
           ws[ref].s = { font: { name: 'Arial', sz: 9 }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border };
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Defaulters');
    XLSX.writeFile(wb, `Fee_Due_List_${today}.xlsx`);
  };

  const totalDue = filteredDefaulters.reduce((s, d) => s + Number(d.balance || 0), 0);
  const totalFiltered = filteredDefaulters.length;

  return (
    <div className="sfd-container">
      {/* Header */}
      <div className="sfd-header-row">
        <h2 className="sfd-title">Fee Due List</h2>
        <div className="sfd-export-btns">
          <button className="sfd-btn-excel" onClick={handleExportExcel} disabled={filteredDefaulters.length === 0}><FaFileExcel /> Export to Excel</button>
        </div>
      </div>

      {/* Summary strip */}
      {filteredDefaulters.length > 0 && (
        <div className="sfd-summary-strip">
          <span>Showing <strong>{totalFiltered}</strong> fee due records</span>
          <span className="sfd-summary-due">Total Due: <strong>₹ {totalDue.toLocaleString()}</strong></span>
        </div>
      )}

      {/* Filters */}
      <div className="sfd-controls">
        <div className="sfd-search-wrapper">
          <FaSearch className="sfd-search-icon" />
          <input
            type="text"
            className="sfd-search-input"
            placeholder="Search by name, father's name, adm. no..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {(isAdmin || isSuperAdmin) && (
          <select className="sfd-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="sfd-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
          <option value="">All Sessions</option>
          {uniqueSessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className="sfd-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {uniqueClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select className="sfd-select" value={filterMedium} onChange={e => setFilterMedium(e.target.value)}>
          <option value="">All Mediums</option>
          {uniqueMediums.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <select className="sfd-select" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}>
          <option value="">All Batches</option>
          {uniqueBatches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <select className="sfd-select" value={filterFeeName} onChange={e => setFilterFeeName(e.target.value)}>
          <option value="">All Fee Names</option>
          {uniqueFeeNames.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <select className="sfd-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All (Pending + Partial)</option>
          <option value="pending">Pending</option>
          <option value="partial">Partial</option>
        </select>

        {(filterSession || filterClass || filterMedium || filterBatch || filterFeeName || searchQuery || filterStatus) && (
          <button className="sfd-btn-clear" onClick={() => {
            setFilterSession(''); setFilterClass(''); setFilterMedium(''); setFilterBatch('');
            setFilterFeeName(''); setSearchQuery(''); setFilterStatus('');
          }}>✕ Clear</button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="sfd-state-msg">Loading defaulters...</div>
      ) : Object.keys(groupedDefaulters).length === 0 ? (
        <div className="sfd-state-msg sfd-empty">No fee due records found.</div>
      ) : (
        <div className="sfd-grouped-container">
          {Object.entries(groupedDefaulters).map(([collegeName, allList]) => {
            const defList = getFilteredList(allList);
            return (
            <div key={collegeName} className="sfd-table-group">
              <h3 className="sfd-group-header">🏫 {collegeName} <span className="sfd-group-count">{defList.length} fee due</span></h3>
              <div className="sfd-table-wrapper">
                <table className="sfd-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th className="sfd-align-left">Student Name</th>
                      <th className="sfd-col-fname">Father's Name</th>
                      <th className="sfd-col-batch">Batch</th>
                      <th className="sfd-col-session">Session</th>
                      <th className="sfd-col-year">Year</th>
                      <th className="sfd-col-class">Class</th>
                      <th className="sfd-col-feename">Fee Name</th>
                      <th className="sfd-col-fee">Fee (₹)</th>
                      <th className="sfd-col-latefee">Late Fee (₹)</th>
                      <th className="sfd-col-totalfee">Total Fee (₹)</th>
                      <th className="sfd-col-deposit">Deposit (₹)</th>
                      <th className="sfd-col-due">Due (₹)</th>
                      <th className="sfd-col-status">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defList.length === 0 ? (
                      <tr>
                        <td colSpan="14" className="sfd-no-results">
                          <span className="sfd-no-results-icon">🔍</span>
                          No matching defaulters found for current filters
                        </td>
                      </tr>
                    ) : defList.map((item, i) => {
                      const rowId   = item._id;
                      const student = item.student || {};
                      const fee      = Number(item.amountDue  || 0);
                      const lateFee  = Number(item.lateFee    || 0);
                      const totalFee = fee + lateFee;
                      const deposit  = Number(item.amountPaid || 0);
                      const due      = Number(item.balance    || 0);
                      const classObj = student.class || {};
                      const className = classObj.name || student.class || '—';
                      const medium    = classObj.medium ? ` (${classObj.medium})` : (student.medium ? ` (${student.medium})` : '');

                      return (
                        <React.Fragment key={rowId}>
                          <tr className="sfd-main-row">
                            <td>{i + 1}</td>
                            <td className="sfd-align-left">
                              <div className="sfd-name-wrapper" onClick={() => setExpandedRow(expandedRow === rowId ? null : rowId)}>
                                <div>
                                  <span style={{ fontWeight: 600 }}>{student.name || '—'}</span>
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{student.admissionNo || ''}</div>
                                </div>
                                <button className="sfd-expand-btn">
                                  {expandedRow === rowId ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                              </div>
                            </td>
                            <td className="sfd-col-fname">{student.fatherName || '—'}</td>
                            <td className="sfd-col-batch">
                              <span className="sfd-badge">{student.admissionYear || '—'}</span>
                            </td>
                            <td className="sfd-col-session">{item.session?.name || '—'}</td>
                            <td className="sfd-col-year">{student.currentYear || '—'}</td>
                            <td className="sfd-col-class">
                              <span style={{ fontWeight: 600 }}>{className}</span>
                              {medium && <div style={{ fontSize: '0.72rem', color: '#7c3aed' }}>{medium}</div>}
                            </td>
                            <td className="sfd-col-feename">
                              <span className="sfd-feename-badge">{item.feeName || '—'}</span>
                            </td>
                            <td className="sfd-col-fee sfd-amt-total">{fee.toLocaleString()}</td>
                            <td className="sfd-col-latefee sfd-amt-late">{lateFee.toLocaleString()}</td>
                            <td className="sfd-col-totalfee sfd-amt-totalfee">{totalFee.toLocaleString()}</td>
                            <td className="sfd-col-deposit sfd-amt-paid">{deposit.toLocaleString()}</td>
                            <td className="sfd-col-due sfd-amt-due">{due.toLocaleString()}</td>
                            <td className="sfd-col-status">
                              <span className={`sfd-status-badge sfd-status-${item.status}`}>
                                {item.status === 'partial' ? 'Partial' : 'Pending'}
                              </span>
                            </td>
                          </tr>

                          {/* Expanded Row: Displays only details hidden by the CSS media queries */}
                          {expandedRow === rowId && (
                            <tr className="sfd-expanded-row">
                              <td colSpan="14" style={{ padding: 0 }}>
                                <div className="sfd-expanded-content">
                                  <div className="sfd-expanded-grid">
                                    <div className="sfd-exp-item sfd-exp-fname">
                                      <span className="sfd-exp-label">Father's Name</span>
                                      <span className="sfd-exp-value">{student.fatherName || '—'}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-batch">
                                      <span className="sfd-exp-label">Batch</span>
                                      <span className="sfd-exp-value">{student.admissionYear || '—'}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-session">
                                      <span className="sfd-exp-label">Session</span>
                                      <span className="sfd-exp-value">{item.session?.name || '—'}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-year">
                                      <span className="sfd-exp-label">Year</span>
                                      <span className="sfd-exp-value">{student.currentYear || '—'}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-class">
                                      <span className="sfd-exp-label">Class</span>
                                      <span className="sfd-exp-value">{className}{medium}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-feename">
                                      <span className="sfd-exp-label">Fee Name</span>
                                      <span className="sfd-exp-value">{item.feeName || '—'}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-fee">
                                      <span className="sfd-exp-label">Fee</span>
                                      <span className="sfd-exp-value sfd-amt-total">₹ {fee.toLocaleString()}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-latefee">
                                      <span className="sfd-exp-label">Late Fee</span>
                                      <span className="sfd-exp-value sfd-amt-late">₹ {lateFee.toLocaleString()}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-totalfee">
                                      <span className="sfd-exp-label">Total Fee</span>
                                      <span className="sfd-exp-value sfd-amt-totalfee">₹ {totalFee.toLocaleString()}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-deposit">
                                      <span className="sfd-exp-label">Deposit</span>
                                      <span className="sfd-exp-value sfd-amt-paid">₹ {deposit.toLocaleString()}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-due">
                                      <span className="sfd-exp-label">Due</span>
                                      <span className="sfd-exp-value sfd-amt-due">₹ {due.toLocaleString()}</span>
                                    </div>
                                    <div className="sfd-exp-item sfd-exp-status">
                                      <span className="sfd-exp-label">Status</span>
                                      <span className="sfd-exp-value">
                                        <span className={`sfd-status-badge sfd-status-${item.status}`}>
                                          {item.status === 'partial' ? 'Partial' : 'Pending'}
                                        </span>
                                      </span>
                                    </div>
                                    <div className="sfd-exp-item">
                                      <span className="sfd-exp-label">Phone</span>
                                      <span className="sfd-exp-value"><FaPhone color="#94a3b8" style={{marginRight:4}}/>{student.contact || 'N/A'}</span>
                                    </div>
                                    <div className="sfd-exp-item">
                                      <span className="sfd-exp-label">Email</span>
                                      <span className="sfd-exp-value"><FaEnvelope color="#94a3b8" style={{marginRight:4}}/>{student.email || 'N/A'}</span>
                                    </div>
                                    <div className="sfd-exp-item" style={{ gridColumn: '1 / -1' }}>
                                      <span className="sfd-exp-label">Address</span>
                                      <span className="sfd-exp-value"><FaMapMarkerAlt color="#94a3b8" style={{marginRight:4}}/>{student.address || 'N/A'}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Defaulter;